import React, { useEffect, useMemo, useRef, useState } from 'react'
import { DownOutlined } from '@ant-design/icons'
import { Form, Select, Button, Spin, Empty, message } from 'antd'
import {
    apiAddTag,
    apiGetTagList,
} from '../../redux/testRun/testRun.service'
import tagStyles from './AsyncTagField.module.css'

const PAGE_SIZE = 20

async function fetchOptionsAPI(query = '', page = 1, projectId) {
    if (!projectId) {
        return { items: [], hasMore: false }
    }
    const params = {
        skip: (page - 1) * PAGE_SIZE,
        limit: PAGE_SIZE,
        projectId,
        sort: 'name',
        sortType: 'asc',
        ...(query ? { search: { name: query } } : {}),
    }
    const res = await apiGetTagList(params)
    const list = res?.data?.data ?? res?.data ?? []
    const items = (list || []).map((x) => ({ value: x._id, label: x.name }))
    const hasMore =
        typeof res?.data?.isNext === 'boolean'
            ? res.data.isNext
            : items.length === PAGE_SIZE
    return { items, hasMore }
}

async function addOptionAPI(label, projectId) {
    const created = await apiAddTag({
        name: label,
        ...(projectId ? { projectId } : {}),
    })
    const respData = created?.data?.data || {}
    return { value: respData?._id ?? '', label: respData?.name ?? '' }
}

function useDebouncedCallback(cb, delay = 400) {
    const t = useRef()
    return (...args) => {
        clearTimeout(t.current)
        t.current = setTimeout(() => cb(...args), delay)
    }
}

function normalizeTagValue(item) {
    if (!item) return null
    const value = item?.value ?? item
    const label = item?.label ?? item?.name ?? String(value ?? '')
    if (value == null || value === '') return null
    return { value, label }
}

function AsyncTagField({
    name = 'tags',
    label = 'Tags',
    hideLabel = false,
    placeholder = 'Search Tag',
    rules = [{ required: true, message: 'Please choose at least one tag' }],
    initialSearch = '',
    form,
    creatable = true,
    closeOnSelect = false,
    blurOnSelect = false,
    maxTagCount = undefined,
    className,
    selectClassName = 'test-rail-select',
    projectId,
    open: openProp = undefined,
    onOpenChange: onOpenChangeProp,
    getPopupContainer,
}) {
    const [options, setOptions] = useState([])
    const [fetching, setFetching] = useState(false)
    const [search, setSearch] = useState(initialSearch)
    const [adding, setAdding] = useState(false)
    const [page, setPage] = useState(1)
    const [hasMore, setHasMore] = useState(true)
    const selectedValues = Form.useWatch(name, form) ?? []
    const searchRef = useRef(search)
    const selectRef = useRef(null)
    const projectIdRef = useRef(projectId)
    searchRef.current = search
    projectIdRef.current = projectId

    // Compact filter / modal selects: collapse after 2 chips unless overridden
    const resolvedMaxTagCount =
        maxTagCount !== undefined
            ? maxTagCount
            : String(selectClassName || '').includes('pool-tag-select')
              ? 2
              : undefined

    const blurSelect = () => {
        requestAnimationFrame(() => {
            selectRef.current?.blur?.()
        })
    }

    const [internalOpen, setInternalOpen] = useState(false)
    const dropdownOpen = openProp !== undefined ? openProp : internalOpen

    const loadOptions = async (q, nextPage = 1, replace = true) => {
        const resolvedProjectId = projectIdRef.current
        if (!resolvedProjectId) {
            setOptions([])
            setHasMore(false)
            setPage(1)
            return
        }
        setFetching(true)
        try {
            const { items, hasMore: more } = await fetchOptionsAPI(
                q,
                nextPage,
                resolvedProjectId
            )
            setOptions((prev) => {
                if (replace) return items
                const map = new Map(
                    prev.map((item) => [String(item.value), item])
                )
                items.forEach((item) => map.set(String(item.value), item))
                return Array.from(map.values())
            })
            setHasMore(more)
            setPage(nextPage)
        } catch (e) {
            message.error(e.message || 'Failed to load options')
        } finally {
            setFetching(false)
        }
    }

    useEffect(() => {
        setOptions([])
        setHasMore(true)
        setPage(1)
        setSearch('')
        loadOptions('', 1, true)
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [projectId])

    const debouncedSearch = useDebouncedCallback((q) => {
        setHasMore(true)
        loadOptions(q, 1, true)
    }, 350)

    const onSearch = (value) => {
        setSearch(value)
        debouncedSearch(value)
    }

    const displayedOptions = useMemo(() => {
        const searchLower = (search || '').trim().toLowerCase()
        const map = new Map()

        options.forEach((option) => {
            if (option?.value == null) return
            if (
                searchLower &&
                !String(option.label || '')
                    .toLowerCase()
                    .includes(searchLower)
            ) {
                return
            }
            map.set(String(option.value), option)
        })

        // Keep selected tags in options only when they match the current search
        // (or when not searching), so search results stay accurate.
        const selectedList = Array.isArray(selectedValues) ? selectedValues : []
        selectedList.forEach((item) => {
            const normalized = normalizeTagValue(item)
            if (!normalized) return
            if (
                searchLower &&
                !String(normalized.label).toLowerCase().includes(searchLower)
            ) {
                return
            }
            map.set(String(normalized.value), normalized)
        })

        return Array.from(map.values())
    }, [options, selectedValues, search])

    const hasExactMatch = useMemo(() => {
        const s = (search || '').trim().toLowerCase()
        if (!s) return true
        return displayedOptions.some(
            (option) => String(option.label).toLowerCase() === s
        )
    }, [search, displayedOptions])

    const onPopupScroll = (e) => {
        const el = e.currentTarget
        const nearBottom =
            el.scrollTop + el.clientHeight >= el.scrollHeight - 16
        if (nearBottom && hasMore && !fetching) {
            loadOptions(searchRef.current, page + 1, false)
        }
    }

    const refreshAllOptionsWith = async (extra) => {
        setFetching(true)
        try {
            const { items } = await fetchOptionsAPI(
                '',
                1,
                projectIdRef.current
            )
            const merged = extra
                ? [extra, ...items.filter((o) => o.value !== extra.value)]
                : items
            setOptions(merged)
            setPage(1)
            setHasMore(true)
        } catch (e) {
            message.error(e.message || 'Failed to refresh options')
        } finally {
            setFetching(false)
        }
    }

    const onAdd = async () => {
        const labelToAdd = (search || '').trim()
        if (!labelToAdd || hasExactMatch) return

        setAdding(true)
        try {
            const created = await addOptionAPI(
                labelToAdd,
                projectIdRef.current
            )

            setOptions((prev) => {
                if (prev.some((p) => p.value === created.value)) return prev
                return [created, ...prev]
            })

            const current = form.getFieldValue(name) || []
            const normalizedCurrent = (Array.isArray(current) ? current : [])
                .map(normalizeTagValue)
                .filter(Boolean)
            form.setFieldsValue({
                [name]: [...normalizedCurrent, created],
            })

            message.success(`Added "${created.label}"`)

            setSearch('')
            await refreshAllOptionsWith(created)
        } catch (e) {
            message.error(e.message || 'Failed to add')
        } finally {
            setAdding(false)
        }
    }

    const handleChange = () => {
        if (closeOnSelect) {
            setSearch('')
            if (blurOnSelect) blurSelect()
            return
        }
        setSearch('')
        loadOptions('', 1, true)
        if (blurOnSelect) blurSelect()
    }

    return (
        <Form.Item
            name={name}
            label={hideLabel ? null : label}
            rules={rules}
            className={className}
            getValueFromEvent={(value) =>
                (Array.isArray(value) ? value : [])
                    .map(normalizeTagValue)
                    .filter(Boolean)
            }
        >
            <Select
                ref={selectRef}
                className={[selectClassName, tagStyles.select]
                    .filter(Boolean)
                    .join(' ')}
                open={dropdownOpen}
                mode="multiple"
                showSearch
                labelInValue
                allowClear
                autoComplete="off"
                placeholder={placeholder}
                filterOption={false}
                onSearch={onSearch}
                searchValue={search}
                options={displayedOptions}
                loading={fetching}
                getPopupContainer={getPopupContainer}
                onPopupScroll={onPopupScroll}
                maxTagCount={resolvedMaxTagCount}
                onMouseDown={(e) => {
                    const target = e?.target
                    if (
                        target &&
                        target.closest &&
                        (target.closest('.ant-select-arrow') ||
                            target.closest('.ant-select-selector'))
                    ) {
                        if (openProp === undefined) setInternalOpen(true)
                        onOpenChangeProp?.(true)
                    }
                }}
                maxTagPlaceholder={
                    resolvedMaxTagCount != null
                        ? (omitted) => `+${omitted.length}`
                        : undefined
                }
                suffixIcon={
                    <DownOutlined
                        style={{ fontSize: 11, color: '#64748b' }}
                    />
                }
                onChange={handleChange}
                notFoundContent={
                    fetching ? (
                        <div style={{ padding: 8, textAlign: 'center' }}>
                            <Spin size="small" />
                        </div>
                    ) : creatable && search?.trim()?.length > 0 ? (
                        <div style={{ textAlign: 'center' }}>
                            Not found for "{search}"
                        </div>
                    ) : (
                        <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} />
                    )
                }
                dropdownRender={(menu) => (
                    <>
                        {menu}
                        {fetching && page > 1 && (
                            <div
                                style={{
                                    padding: 8,
                                    textAlign: 'center',
                                }}
                            >
                                <Spin size="small" />
                            </div>
                        )}
                        {!hasExactMatch &&
                            creatable &&
                            search?.trim()?.length > 0 && (
                                <div
                                    style={{
                                        display: 'flex',
                                        gap: 8,
                                        padding: 8,
                                    }}
                                >
                                    <Button
                                        type="default"
                                        block
                                        loading={adding}
                                        onMouseDown={(e) => e.preventDefault()}
                                        onClick={onAdd}
                                    >
                                        Add "{search}"
                                    </Button>
                                </div>
                            )}
                    </>
                )}
                autoClearSearchValue={false}
                onOpenChange={(visible) => {
                    if (openProp === undefined) setInternalOpen(visible)
                    onOpenChangeProp?.(visible)

                    if (!visible) {
                        setSearch('')
                        if (blurOnSelect) blurSelect()
                        return
                    }
                    if (!options.length) {
                        loadOptions('', 1, true)
                    }
                }}
                listHeight={256}
                menuItemSelectedIcon={null}
            />
        </Form.Item>
    )
}

export default AsyncTagField
